import type { PrepLine } from '@morales/shared';

/**
 * The prep card (decision 179), laid out the way the Sheets' Prep.gs does it (JL, 09-19):
 * - one letter sheet, four 4 × 5¼ in panels a side, folded on the dashed lines;
 * - TOTALES first: every order added together, item by item (×N: how many orders take it);
 * - an order with more than three lines gets a panel to itself; small orders share, up to three to a panel;
 * - each panel takes the biggest type its orders allow, in one wide column or two narrow ones;
 * - an order never splits across panels, unless it is too long for a whole panel even in small type (then it runs on,
 *   marked (cont.)); front side panels 1-4, back 5-8; past eight panels the lightest panels share first.
 * It measures what it lays out, so it works on a container that is in the page (off screen) with real inches.
 */
export interface CardOrder {
  key: string;
  customer: string;
  lines: PrepLine[];
}
export interface CardWords {
  totals: string;
  cont: string;
}
export interface CardResult {
  panels: number;
  sides: number;
  sheets: number;
  minPt: number;
  maxPt: number;
  spilled: boolean;
}

const SMALL_MAX = 3;
const CARD_MAX_S = 2.0;
const ORDER_FONTS = 4;
/** biggest first; 1 = 8.5 pt */
const CARD_SIZES = [2.2, 2.1, 2, 1.9, 1.8, 1.72, 1.64, 1.56, 1.48, 1.41, 1.35, 1.3, 1.24, 1.18, 1.12, 1.06, 1, 0.95, 0.9, 0.85, 0.8, 0.76, 0.72];

export const enc = (s: unknown) => String(s ?? '').split('&').join('&amp;').split('<').join('&lt;').split('>').join('&gt;').split('"').join('&quot;');
export const fq = (q: number) => String(Math.round(q * 100) / 100);
export const el = (html: string): HTMLElement => {
  const d = document.createElement('div');
  d.innerHTML = html;
  return d.firstChild as HTMLElement;
};
/** the item, with a boxed LB / EACH in front of a line sold by the pound or the piece (never read as cases) */
export const nm = (item: string, tag: string | null) => (tag ? `<span class="u">${enc(tag)}</span> ${enc(item)}` : enc(item));

export interface PullRow {
  name: string;
  tag: PrepLine['tag'];
  qty: number;
  orders: number;
}
/** Every order added together, item by item (the same item in another unit is its own row). */
export function pullOf(orders: CardOrder[]): PullRow[] {
  const m = new Map<string, PullRow & { keys: Set<string> }>();
  for (const o of orders) {
    for (const l of o.lines) {
      const k = `${l.name}\u0000${l.tag ?? ''}`;
      const row = m.get(k) ?? { name: l.name, tag: l.tag, qty: 0, orders: 0, keys: new Set<string>() };
      row.qty = Math.round((row.qty + l.qty) * 100) / 100;
      row.keys.add(o.key);
      m.set(k, row);
    }
  }
  return [...m.values()].map(({ keys, ...r }) => ({ ...r, orders: keys.size })).sort((a, b) => a.name.localeCompare(b.name) || (a.tag ?? '').localeCompare(b.tag ?? ''));
}

/** Orders grouped into panels: big ones alone, small ones three to a panel, in the list's order; past `max` the two lightest share. */
export function cardUnits(orders: CardOrder[], max: number): CardOrder[][] {
  const big: { i: number; t: CardOrder }[] = [];
  const small: { i: number; t: CardOrder }[] = [];
  orders.forEach((t, i) => (t.lines.length > SMALL_MAX ? big : small).push({ i, t }));
  const units = big.map((o) => [o]);
  for (let k = 0; k < small.length; k += 3) units.push(small.slice(k, k + 3));
  units.sort((a, b) => a[0]!.i - b[0]!.i);
  const lines = (u: { t: CardOrder }[]) => u.reduce((a, o) => a + o.t.lines.length, 0);
  while (units.length > max) {
    let best: { i: number; j: number; n: number } | null = null;
    for (let i = 0; i < units.length; i++) {
      for (let j = i + 1; j < units.length; j++) {
        const n = lines(units[i]!) + lines(units[j]!);
        if (n <= 24 && (!best || n < best.n)) best = { i, j, n };
      }
    }
    if (!best) break;
    units[best.i] = units[best.i]!.concat(units[best.j]!);
    units.splice(best.j, 1);
  }
  return units.map((u) => u.map((o) => o.t));
}

type Panel = HTMLElement & { who: string[]; k: number; s: number };
type Block = { head: HTMLElement; rows: HTMLElement[]; cont: string };

/** Lays the card out in `out` (emptied first). TOTALES keeps 9.5 pt or more unless that would take a second sheet. */
export function buildCard(out: HTMLElement, orders: CardOrder[], words: CardWords): CardResult {
  let r = layCard(out, orders, words, 1.12);
  if (r.panels > 8) r = layCard(out, orders, words, 0.8);
  return r;
}

function layCard(out: HTMLElement, P: CardOrder[], words: CardWords, tmin: number): CardResult {
  out.innerHTML = '';
  const panels: Panel[] = [];
  let spare: Panel | null = null;
  const fontOf = new Map<CardOrder, string>();
  P.forEach((t, i) => fontOf.set(t, ` f${i % ORDER_FONTS}`));
  function newPanel(): Panel {
    if (spare) {
      const p = spare;
      spare = null;
      return p;
    }
    const k = panels.length % 4;
    if (k === 0) {
      const pg = el('<div class="pg sheet4"></div>');
      out.appendChild(pg);
      for (let i = 0; i < 4; i++) pg.appendChild(el(`<div class="pan p${i}"><div class="cols"></div></div>`));
    }
    const pan = (out.lastChild as HTMLElement).children[k] as Panel;
    pan.who = [];
    pan.k = k;
    pan.s = 1;
    panels.push(pan);
    return pan;
  }
  function setCols(pan: Panel, s: number, nc: number) {
    pan.style.setProperty('--s', String(s));
    pan.className = `pan p${pan.k} c${nc}`;
    pan.querySelector('.cols')!.innerHTML = '<div class="col"></div>'.repeat(nc);
    pan.s = s;
  }
  const colsOf = (pan: Panel) => [...pan.querySelectorAll<HTMLElement>('.col')];
  const fits = (n: HTMLElement, c: HTMLElement) => n.getBoundingClientRect().bottom <= c.getBoundingClientRect().bottom - 2;
  const lastUsed = (cs: HTMLElement[]) => {
    for (let i = cs.length - 1; i >= 0; i--) if (cs[i]!.children.length) return i;
    return 0;
  };
  const row = (f: string, l: PrepLine) =>
    el(`<div class="r${f}"><span class="bx"></span><span class="q">${fq(l.qty)}</span><span class="it">${nm(l.name, l.tag)}${l.note ? `<span class="ds">${enc(l.note)}</span>` : ''}</span></div>`);
  const num = new Map<CardOrder, number>();
  const blk = (t: CardOrder): Block => {
    const f = fontOf.get(t) ?? '';
    const no = num.get(t);
    // the name only: no invoice number, line count or quantity (JL 09-19)
    return {
      head: el(`<div class="ch o${f}">${no}. ${enc(t.customer)}</div>`),
      rows: t.lines.map((l) => row(f, l)),
      cont: `<div class="ch o${f}">${no}. ${enc(t.customer)} <small>${enc(words.cont)}</small></div>`,
    };
  };
  /** head and rows into this panel after what is already there, flowing across its columns; all or nothing */
  function tryPlace(pan: Panel, b: Block): boolean {
    const cs = colsOf(pan);
    const L = cs.length - 1;
    let c = lastUsed(cs);
    const added: HTMLElement[] = [];
    const put = (x: HTMLElement) => {
      cs[c]!.appendChild(x);
      added.push(x);
      return fits(x, cs[c]!);
    };
    const undo = () => {
      added.forEach((x) => x.remove());
      return false;
    };
    if (!put(b.head)) {
      if (c === L) return undo();
      added.pop()!.remove();
      c++;
      if (!put(b.head)) return undo();
    }
    for (let i = 0; i < b.rows.length; i++) {
      if (put(b.rows[i]!)) continue;
      added.pop()!.remove();
      if (c === L) return undo();
      c++;
      // never leave a heading alone at the bottom
      if (i === 0) {
        added.pop()!.remove();
        if (!put(b.head)) return undo();
      }
      if (!put(b.rows[i]!)) return undo();
    }
    return true;
  }
  // one wide column reads like two narrow ones at 20% bigger type (narrow columns wrap), so rank them that way
  const cands: { s: number; nc: number; score: number }[] = [];
  CARD_SIZES.filter((s) => s <= CARD_MAX_S).forEach((s) => [1, 2].forEach((nc) => cands.push({ s, nc, score: s * (nc === 1 ? 1.2 : 1) })));
  cands.sort((a, b) => b.score - a.score || a.nc - b.nc);
  let spilled = false;
  /** run a list on across columns and panels at a fixed size, (cont.) at each break */
  function runOn(p0: Panel, b: Block, s: number, nc: number, who: string): Panel {
    let p = p0;
    let cs = colsOf(p);
    let c = lastUsed(cs);
    p.who.push(who);
    const next = () => {
      if (c < cs.length - 1) c++;
      else {
        p = newPanel();
        setCols(p, s, nc);
        p.who.push(who);
        cs = colsOf(p);
        c = 0;
      }
    };
    cs[c]!.appendChild(b.head);
    if (!fits(b.head, cs[c]!)) {
      b.head.remove();
      next();
      cs[c]!.appendChild(b.head);
    }
    b.rows.forEach((r) => {
      cs[c]!.appendChild(r);
      if (!fits(r, cs[c]!)) {
        r.remove();
        next();
        cs[c]!.appendChild(el(b.cont));
        cs[c]!.appendChild(r);
      }
    });
    return p;
  }
  // TOTALES first (JL 09-19): its own panel if it fits at the minimum size or more; else split evenly over two panels
  const pull = pullOf(P);
  if (pull.length) {
    const tb = (): Block => ({
      head: el(`<div class="ch o">${enc(words.totals)}</div>`),
      rows: pull.map((x) => el(`<div class="r"><span class="bx"></span><span class="q">${fq(x.qty)}</span><span class="it">${nm(x.name, x.tag)}</span>${x.orders > 1 ? `<span class="k">×${x.orders}</span>` : ''}</div>`)),
      cont: `<div class="ch o">${enc(words.totals)} <small>${enc(words.cont)}</small></div>`,
    });
    const pan = newPanel();
    let ok = false;
    for (const c of cands) {
      if (c.s < tmin) continue;
      setCols(pan, c.s, c.nc);
      if (tryPlace(pan, tb())) {
        ok = true;
        break;
      }
    }
    if (ok) pan.who.push(words.totals);
    else {
      const pan2 = newPanel();
      const half = Math.ceil(pull.length / 2);
      for (const c of cands) {
        if (c.s < tmin) continue;
        setCols(pan, c.s, c.nc);
        setCols(pan2, c.s, c.nc);
        const b = tb();
        if (tryPlace(pan, { head: b.head, rows: b.rows.slice(0, half), cont: b.cont }) && tryPlace(pan2, { head: el(b.cont), rows: b.rows.slice(half), cont: b.cont })) {
          ok = true;
          break;
        }
      }
      if (ok) {
        pan.who.push(words.totals);
        pan2.who.push(words.totals);
      } else {
        // more than two panels' worth: run on
        spare = pan2;
        setCols(pan2, tmin, 2);
        setCols(pan, tmin, 2);
        runOn(pan, tb(), tmin, 2, words.totals);
      }
    }
  }
  const units = cardUnits(P, Math.max(1, 8 - panels.length));
  let n = 0;
  units.forEach((u) => u.forEach((t) => num.set(t, ++n)));
  units.forEach((u) => {
    const pan = newPanel();
    let ok = false;
    for (const c of cands) {
      setCols(pan, c.s, c.nc);
      if (u.every((t) => tryPlace(pan, blk(t)))) {
        ok = true;
        break;
      }
    }
    if (ok) {
      u.forEach((t) => pan.who.push(t.customer));
      return;
    }
    // too long for one panel even in small type: run it on, column after column, (cont.) on each
    spilled = true;
    let p = pan;
    setCols(p, 0.9, 2);
    u.forEach((t) => {
      p = runOn(p, blk(t), 0.9, 2, t.customer);
    });
  });
  const pts = panels.map((p) => Math.round(p.s * 8.5 * 2) / 2);
  return {
    panels: panels.length,
    sides: Math.ceil(panels.length / 4),
    sheets: Math.ceil(panels.length / 8),
    minPt: pts.length ? Math.min(...pts) : 0,
    maxPt: pts.length ? Math.max(...pts) : 0,
    spilled,
  };
}
